// react and js
import * as Yup from "yup";
import { React, useContext, useState, useRef, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// mui
import {
  Link,
  Stack,
  IconButton,
  InputAdornment,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";

// components
import RequestMessage from "./RequestMessage";
import CardOutlet from "./CardOutlet";
import Iconify from "./Iconify";
import backendRequest from "./BackendRequest";

// hooks
import appContext from "../hooks/appContext";
import { stateStorageInit } from "../hooks/stateStorageInit";
import useRecaptchaV3 from "../hooks/useRecaptchaV3";

// i18n
import { localizeWebsiteUrl, DEFAULT_LANGUAGE } from "../i18n";

// layouts

// pages

// theme
import {
  AuthenticationButton,
  AuthenticationTextField,
  B3Small,
  ModalContentSwitch,
} from "../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
// Pull a dashboard_access_token out of any auth response, whether the
// backend nests it under .answer (current convention) or returns it flat
// at the root (Signova convention). Returns "" if not found.
function extractDashboardAccessToken(content) {
  if (!content) return "";
  if (content.answer && content.answer.dashboard_access_token) {
    return content.answer.dashboard_access_token;
  }
  if (content.dashboard_access_token) {
    return content.dashboard_access_token;
  }
  return "";
}

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export function Login({ caller }) {
  Login.propTypes = {
    caller: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const [showPassword, setShowPassword] = useState(false);
  const executeRecaptchaV3 = useRecaptchaV3();

  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);

  const [returnMessage, setReturnMessage] = useState("");

  useEffect(() => {
    if (!error && !warning && !success) return undefined;
    const timer = setTimeout(() => {
      setError(false);
      setWarning(false);
      setSuccess(false);
      setReturnMessage("");
    }, 60000);
    return () => clearTimeout(timer);
  }, [error, warning, success]);

  const ValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("components.authentication.validation.emailInvalid"))
      .required(t("components.authentication.validation.emailRequired")),
    password: Yup.string().required(
      t("components.authentication.validation.passwordRequired")
    ),
  });

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleLogin = async (values, setSubmitting) => {
    setError(false);
    const myLocalSettingStorage = JSON.parse(
      sessionStorage.getItem("myLocalSettingStorage")
    );

    const recaptchaValue = await executeRecaptchaV3("login");
    const sendData = {
      request_type: "checkout",
      route_info: "authentication",
      client: {
        mode: "login",
        email: values.email,
        password: values.password,
        request_known_accounts: "1",
        recaptcha_value: recaptchaValue,
      },
    };
    backendRequest(process.env.REACT_APP_URL_API_CHECKOUT, sendData)
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("answer");

        if (checkKey === true) {
          if (caller === "distribution") {
            // Perform after login checks
            let sameProductFound = false;
            contentFromBackend.dashboard_subscriptions.subscriptions.forEach(
              (subscription) => {
                if (
                  myLocalSettingStorage.distributionPlan.product_tag ===
                  subscription.product.product_tag
                ) {
                  if (
                    subscription.plan.payment_state === "distribution_inactive"
                  ) {
                    sameProductFound = false;
                  } else if (
                    subscription.plan.payment_state === "distribution_active" ||
                    subscription.plan.payment_state ===
                      "distribution_approval_pending"
                  ) {
                    sameProductFound = true;
                  }
                }
              }
            );

            // Error step handling
            if (sameProductFound === false) {
              // Continue — skip Trading Accounts step if product doesn't require it
              const nextStep = myLocalSettingStorage.distributionProductAccountRequired === "0" ? 3 : 2;
              setStateStorage((previousState) => ({
                ...previousState,
                distributionCurrentStep: nextStep,
                userInformation: contentFromBackend,
                ...(nextStep === 3 && {
                  distributionAccountType: "",
                  distributionAccountNumber: "",
                  distributionTradingServer: "",
                }),
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.distributionCurrentStep = nextStep;
              newLocalSettingStorage.userInformation = contentFromBackend;
              if (nextStep === 3) {
                newLocalSettingStorage.distributionAccountType = "";
                newLocalSettingStorage.distributionAccountNumber = "";
                newLocalSettingStorage.distributionTradingServer = "";
              }
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            } else if (sameProductFound === true) {
              setStateStorage((previousState) => ({
                ...previousState,
                distributionCurrentStep: 1,
                distributionErrorStep: 1,
                distributionErrorStepCode: "same_product_subscribed",
                userInformation: contentFromBackend,
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.distributionCurrentStep = 1;
              newLocalSettingStorage.distributionErrorStep = 1;
              newLocalSettingStorage.distributionErrorStepCode =
                "same_product_subscribed";
              newLocalSettingStorage.userInformation = contentFromBackend;
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            }
          } else if (caller === "distribution-tick-data") {
            // Perform after login checks
            let sameProductFound = false;
            contentFromBackend.dashboard_subscriptions.subscriptions.forEach(
              (subscription) => {
                if (
                  myLocalSettingStorage.distributionTickDataPlan.product_tag ===
                  subscription.product.product_tag
                ) {
                  if (
                    subscription.plan.payment_state === "distribution_inactive"
                  ) {
                    sameProductFound = false;
                  } else if (
                    subscription.plan.payment_state === "distribution_active" ||
                    subscription.plan.payment_state ===
                      "distribution_approval_pending"
                  ) {
                    sameProductFound = true;
                  }
                }
              }
            );

            // Error step handling
            if (sameProductFound === false) {
              // Continue
              setStateStorage((previousState) => ({
                ...previousState,
                distributionTickDataCurrentStep: 2,
                userInformation: contentFromBackend,
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.distributionTickDataCurrentStep = 2;
              newLocalSettingStorage.userInformation = contentFromBackend;
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            } else if (sameProductFound === true) {
              setStateStorage((previousState) => ({
                ...previousState,
                distributionTickDataCurrentStep: 1,
                distributionTickDataErrorStep: 1,
                distributionTickDataErrorStepCode:
                  "same_tick_data_product_subscribed",
                userInformation: contentFromBackend,
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.distributionTickDataCurrentStep = 1;
              newLocalSettingStorage.distributionTickDataErrorStep = 1;
              newLocalSettingStorage.distributionTickDataErrorStepCode =
                "same_tick_data_product_subscribed";
              newLocalSettingStorage.userInformation = contentFromBackend;
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            }
          } else if (caller === "promotion") {
            // Perform after login checks
            let promotionPeriodOver = false;
            if (
              contentFromBackend.dashboard_promotion.message.includes(
                "access denied"
              )
            ) {
              promotionPeriodOver = true;
            }

            // Perform after login checks
            let promoterAlreadyInUse = false;
            if (
              contentFromBackend.dashboard_promotion.message.includes("success")
            ) {
              contentFromBackend.dashboard_promotion.promotion.forEach(
                (partner) => {
                  if (
                    partner.promotion_broker_details.broker_id ===
                    myLocalSettingStorage.promotionPromotionPartner.broker_id
                  ) {
                    if (partner.subscriptions[0].accounts.length !== 0) {
                      promoterAlreadyInUse = true;
                    }
                  }
                }
              );
            }

            // Error step handling
            if (
              promotionPeriodOver === false &&
              promoterAlreadyInUse === false
            ) {
              // Continue
              setStateStorage((previousState) => ({
                ...previousState,
                promotionCurrentStep: 2,
                userInformation: contentFromBackend,
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.promotionCurrentStep = 2;
              newLocalSettingStorage.userInformation = contentFromBackend;
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            } else if (promotionPeriodOver === true) {
              setStateStorage((previousState) => ({
                ...previousState,
                promotionCurrentStep: 1,
                promotionErrorStep: 1,
                promotionErrorStepCode: "promotion_period_over",
                userInformation: contentFromBackend,
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.promotionCurrentStep = 1;
              newLocalSettingStorage.promotionErrorStep = 1;
              newLocalSettingStorage.promotionErrorStepCode =
                "promotion_period_over";
              newLocalSettingStorage.userInformation = contentFromBackend;
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            } else if (promoterAlreadyInUse === true) {
              setStateStorage((previousState) => ({
                ...previousState,
                promotionCurrentStep: 1,
                promotionErrorStep: 1,
                promotionErrorStepCode: "promoter_already_in_use",
                userInformation: contentFromBackend,
              }));

              let newLocalSettingStorage = JSON.parse(
                sessionStorage.getItem("myLocalSettingStorage")
              );
              if (newLocalSettingStorage === null) {
                newLocalSettingStorage = stateStorageInit;
              }

              newLocalSettingStorage.promotionCurrentStep = 1;
              newLocalSettingStorage.promotionErrorStep = 1;
              newLocalSettingStorage.promotionErrorStepCode =
                "promoter_already_in_use";
              newLocalSettingStorage.userInformation = contentFromBackend;
              sessionStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(newLocalSettingStorage)
              );
            }
          }

          setError(false);
        } else if (
          contentFromBackend.message &&
          contentFromBackend.message.includes("please check your inbox")
        ) {
          // Forward to Verify your email mask instead of showing
          // the error inline in the login form. Pull the dashboard
          // access token out of whatever shape the backend returns so
          // the existing is_email_approved polling in ApprovalAwaiting
          // can pick the verification up automatically (same UX as the
          // create-account flow).
          const userInformationStub = {
            ...contentFromBackend,
            answer: {
              ...(contentFromBackend.answer || {}),
              dashboard_access_token:
                extractDashboardAccessToken(contentFromBackend),
            },
            account_settings: {
              ...(contentFromBackend.account_settings || {}),
              config_data: {
                ...((contentFromBackend.account_settings &&
                  contentFromBackend.account_settings.config_data) ||
                  {}),
                config_data_client: {
                  ...((contentFromBackend.account_settings &&
                    contentFromBackend.account_settings.config_data &&
                    contentFromBackend.account_settings.config_data
                      .config_data_client) ||
                    {}),
                  email: values.email,
                },
              },
            },
          };

          setStateStorage((previousState) => ({
            ...previousState,
            authState: "approval_awaiting",
            userInformation: userInformationStub,
            performRescroll: true,
          }));

          let newLocalSettingStorage = JSON.parse(
            sessionStorage.getItem("myLocalSettingStorage")
          );
          if (newLocalSettingStorage === null) {
            newLocalSettingStorage = stateStorageInit;
          }

          newLocalSettingStorage.authState = "approval_awaiting";
          newLocalSettingStorage.userInformation = userInformationStub;
          newLocalSettingStorage.performRescroll = true;

          sessionStorage.setItem(
            "myLocalSettingStorage",
            JSON.stringify(newLocalSettingStorage)
          );

          setSubmitting(false);
        } else {
          setStateStorage((previousState) => ({
            ...previousState,
          }));
          combineReturnMessage(contentFromBackend, setReturnMessage);

          setError(true);
        }
        setSubmitting(false);
      })
      // eslint-disable-next-line no-console
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage messageKey="components.authentication.errors.unknown" />);
          setError(true);
          setSubmitting(false);
        }
      });
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleLogin(values, setSubmitting);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  useEffect(() => {
    if (!error && !warning && !success) return;
    setError(false);
    setWarning(false);
    setSuccess(false);
    setReturnMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.email, values.password]);

  return (
    <CardOutlet
      variant="outlet"
      backgroundProp="#F2F2F2"
      title={t("components.authentication.login.title")}
      subheader={t("components.authentication.login.subheader")}
      mainOutlet={
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={0} sx={{ mt: 1 }}>
              <Box>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.email && errors.email) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.email && errors.email) === true
                    ? errors.email
                    : t("components.authentication.emailLabel")}
                </Typography>
                <AuthenticationTextField
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type="email"
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                />
              </Box>

              <Box>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.password && errors.password) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.password && errors.password) === true
                    ? errors.password
                    : t("components.authentication.passwordLabel")}
                </Typography>
                <AuthenticationTextField
                  autoComplete="off" // Avoids chrome warning
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  {...getFieldProps("password")}
                  error={Boolean(touched.password && errors.password)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify
                            icon={
                              showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box>
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                  marginTopProp={
                    error === true || warning === true || success === true
                      ? 1
                      : 0
                  }
                  marginBottomProp={0}
                  fullWidth
                />
              </Box>

              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={6} md={6}>
                  <B3Small
                    fullWidth
                    size="large"
                    variant="outlined"
                    onClick={() => {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "create",
                        performRescroll: true,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "create";
                      newLocalSettingStorage.performRescroll = true;
                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    }}
                  >
                    {t("components.authentication.login.createNewProfile")}
                  </B3Small>
                </Grid>

                <Grid item xs={6} md={6}>
                  <B3Small
                    fullWidth
                    size="large"
                    variant="outlined"
                    onClick={() => {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "password_forgot",
                        performRescroll: true,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "password_forgot";
                      newLocalSettingStorage.performRescroll = true;
                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    }}
                  >
                    {t("components.authentication.login.resetPassword")}
                  </B3Small>
                </Grid>
              </Grid>
            </Stack>

            <AuthenticationButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{ mt: 1 }}
            >
              {t("components.authentication.login.loginButton")}
            </AuthenticationButton>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 1,
                mb: -2,
              }}
            >
              <Typography variant="contentSmall">
                {/* eslint-disable-next-line */}
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    if (caller === "distribution") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        distributionCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.distributionCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    } else if (caller === "distribution-tick-data") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        distributionTickDataCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.distributionTickDataCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    } else if (caller === "promotion") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        promotionCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.promotionCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    }
                  }}
                  underline="none"
                  variant="contentSmallLink"
                  color="inherit"
                >
                  {t("components.authentication.backToPreviousStep")}
                </Link>
              </Typography>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}

const STRENGTH_COLORS = {
  weak: "#E53935",
  medium: "#F19B37",
  strong: "#4CAF50",
};
const STRENGTH_LABEL_KEYS = {
  weak: "components.authentication.passwordStrength.weak",
  medium: "components.authentication.passwordStrength.medium",
  strong: "components.authentication.passwordStrength.strong",
};
const STRENGTH_WIDTHS = { weak: "33%", medium: "66%", strong: "100%" };

function getPasswordStrength(metCount) {
  if (metCount <= 2) return "weak";
  if (metCount <= 3) return "medium";
  return "strong";
}

function PasswordRequirements({ password }) {
  const { t } = useTranslation();
  const passwordValue = password || "";
  const checks = [
    {
      met: passwordValue.length >= 8,
      label: t("components.authentication.passwordRequirements.minLength"),
    },
    {
      met: /[A-Z]/.test(passwordValue),
      label: t("components.authentication.passwordRequirements.uppercase"),
    },
    {
      met: /[a-z]/.test(passwordValue),
      label: t("components.authentication.passwordRequirements.lowercase"),
    },
    {
      met: /[0-9]/.test(passwordValue),
      label: t("components.authentication.passwordRequirements.number"),
    },
  ];
  const metCount = checks.filter((c) => c.met).length;
  const strength =
    passwordValue.length === 0 ? null : getPasswordStrength(metCount);

  return (
    <Box sx={{ mt: 1, mb: 1.5 }}>
      {strength && (
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: "#E0E0E0",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                borderRadius: 2,
                backgroundColor: STRENGTH_COLORS[strength],
                width: STRENGTH_WIDTHS[strength],
                transition:
                  "width 0.3s ease, background-color 0.3s ease",
              }}
            />
          </Box>
          <Typography
            variant="contentSmall"
            sx={{
              color: STRENGTH_COLORS[strength],
              mt: 0.5,
              display: "block",
            }}
          >
            {t(STRENGTH_LABEL_KEYS[strength])}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "center",
          rowGap: 0.5,
          columnGap: 1.5,
        }}
      >
        {checks.map(({ met, label }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Iconify
              icon={
                met
                  ? "eva:checkmark-circle-2-fill"
                  : "eva:radio-button-off-outline"
              }
              sx={{ fontSize: 14, color: met ? "#4CAF50" : "#BDBDBD" }}
            />
            <Typography
              variant="contentSmall"
              sx={{ color: met ? "#4CAF50" : "#9E9E9E" }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

PasswordRequirements.propTypes = {
  password: PropTypes.string,
};

export function CreateAccount({ modeContent, caller }) {
  CreateAccount.propTypes = {
    modeContent: PropTypes.any.isRequired,
    caller: PropTypes.any.isRequired,
  };

  const { t, i18n } = useTranslation();
  const activeLang = i18n.language || DEFAULT_LANGUAGE;
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const wrapperRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const executeRecaptchaV3 = useRecaptchaV3();

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);

  const [returnMessage, setReturnMessage] = useState("");

  const [formPpTac, setFormPpTac] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setError(false);
        setWarning(false);
        setSuccess(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, {
      passive: false,
      capture: false,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  const ValidationSchema = Yup.object().shape({
    forename:
      caller === "promotion"
        ? Yup.string()
            .matches(/^[a-zA-ZÀ-ÖÙ-öù-ÿĀ-žḀ-ỿ0-9\s\-.]+$/)
            .max(40)
            .required()
        : Yup.string(),
    surname:
      caller === "promotion"
        ? Yup.string()
            .matches(/^[a-zA-ZÀ-ÖÙ-öù-ÿĀ-žḀ-ỿ0-9\s\-.]+$/)
            .max(40)
            .required()
        : Yup.string(),
    email: Yup.string()
      .email(t("components.authentication.validation.emailInvalid"))
      .required(t("components.authentication.validation.emailRequired")),
    password: Yup.string()
      .required(t("components.authentication.validation.passwordRequired"))
      .min(8, t("components.authentication.validation.passwordMinLength"))
      .max(64, t("components.authentication.validation.passwordMaxLength"))
      .matches(
        /[0-9]/,
        t("components.authentication.validation.passwordRequiresNumber")
      )
      .matches(
        /[a-z]/,
        t("components.authentication.validation.passwordRequiresLowercase")
      )
      .matches(
        /[A-Z]/,
        t("components.authentication.validation.passwordRequiresUppercase")
      )
      .notOneOf(
        [Yup.ref("passwordOld"), null],
        t("components.authentication.validation.passwordSameAsOld")
      ),
    passwordRepeat: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        t("components.authentication.validation.passwordsMustMatch")
      )
      .required(t("components.authentication.validation.passwordRequired")),
  });

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleCreate = async (values, setSubmitting) => {
    setError(false);

    const recaptchaValue = await executeRecaptchaV3("create_account");
    const sendData = {
      request_type: "checkout",
      route_info: "authentication",
      client: {
        mode: modeContent,
        forename: values.forename,
        surname: values.surname,
        email: values.email,
        password: values.password,
        legal_agreement: formPpTac ? "1" : "0",
        recaptcha_value: recaptchaValue,
      },
    };

    backendRequest(process.env.REACT_APP_URL_API_CHECKOUT, sendData)
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("answer");

        if (checkKey === true) {
          setStateStorage((previousState) => ({
            ...previousState,
            authState: "approval_awaiting",
            userInformation: contentFromBackend,
          }));

          let newLocalSettingStorage = JSON.parse(
            sessionStorage.getItem("myLocalSettingStorage")
          );
          if (newLocalSettingStorage === null) {
            newLocalSettingStorage = stateStorageInit;
          }

          newLocalSettingStorage.authState = "approval_awaiting";
          newLocalSettingStorage.userInformation = contentFromBackend;
          sessionStorage.setItem(
            "myLocalSettingStorage",
            JSON.stringify(newLocalSettingStorage)
          );

          setError(false);

          setStateStorage((previousState) => ({
            ...previousState,
            performRescroll: true,
          }));
        } else {
          combineReturnMessage(contentFromBackend, setReturnMessage);
          setError(true);
        }
        setSubmitting(false);
      })
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage messageKey="components.authentication.errors.unknown" />);
          setError(true);
          setSubmitting(false);
        }
      });
  };

  const formik = useFormik({
    initialValues: {
      forename: "",
      surname: "",
      email: "",
      password: "",
      passwordRepeat: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleCreate(values, setSubmitting);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  return (
    <CardOutlet
      variant="outlet"
      backgroundProp="#F2F2F2"
      title={t("components.authentication.createAccount.title")}
      subheader={t("components.authentication.createAccount.subheader")}
      mainOutlet={
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={0} sx={{ mt: 1 }}>
              {caller === "promotion" ? (
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={6} lg={6}>
                      <Box>
                        <Typography
                          variant="contentSmallBold"
                          sx={{
                            color:
                              Boolean(touched.forename && errors.forename) ===
                              true
                                ? "red"
                                : "none",
                          }}
                        >
                          {Boolean(touched.forename && errors.forename) === true
                            ? t("components.authentication.validation.forenameRequired")
                            : t("components.authentication.forenameLabel")}
                        </Typography>
                        <AuthenticationTextField
                          fullWidth
                          size="small"
                          type="text"
                          {...getFieldProps("forename")}
                          sx={{ mt: "4px", mb: 1 }}
                          error={Boolean(touched.forename && errors.forename)}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <Box>
                        <Typography
                          variant="contentSmallBold"
                          sx={{
                            color:
                              Boolean(touched.surname && errors.surname) ===
                              true
                                ? "red"
                                : "none",
                          }}
                        >
                          {Boolean(touched.surname && errors.surname) === true
                            ? t("components.authentication.validation.surnameRequired")
                            : t("components.authentication.surnameLabel")}
                        </Typography>
                        <AuthenticationTextField
                          fullWidth
                          size="small"
                          type="text"
                          {...getFieldProps("surname")}
                          sx={{ mt: "4px", mb: 1 }}
                          error={Boolean(touched.surname && errors.surname)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ) : null}

              <Box>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.email && errors.email) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.email && errors.email) === true
                    ? errors.email
                    : t("components.authentication.emailLabel")}
                </Typography>

                <AuthenticationTextField
                  autoComplete="new-password"
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type="email"
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                />
              </Box>

              <Box>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.password && errors.password) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.password && errors.password) === true
                    ? errors.password
                    : t("components.authentication.passwordLabel")}
                </Typography>
                <AuthenticationTextField
                  autoComplete="new-password" // Avoids chrome warning
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  {...getFieldProps("password")}
                  error={Boolean(touched.password && errors.password)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify
                            icon={
                              showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <PasswordRequirements password={values.password} />

              <Box>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(
                        touched.passwordRepeat && errors.passwordRepeat
                      ) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.passwordRepeat && errors.passwordRepeat) ===
                  true
                    ? errors.passwordRepeat
                    : t("components.authentication.repeatPasswordLabel")}
                </Typography>

                <AuthenticationTextField
                  autoComplete="new-password" // Avoids chrome warning
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  {...getFieldProps("passwordRepeat")}
                  error={Boolean(
                    touched.passwordRepeat && errors.passwordRepeat
                  )}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify
                            icon={
                              showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    pr: 1,
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="contentSmall">
                    {t("components.authentication.legal.intro")}
                    <Link
                      href={localizeWebsiteUrl(
                        process.env.REACT_APP_URL_FRONTEND_PRIVACY_POLICY,
                        activeLang
                      )}
                      target="_blank"
                      underline="none"
                      variant="contentSmallLink"
                      color="inherit"
                    >
                      {` ${t("components.authentication.legal.privacyPolicy")} `}
                    </Link>
                    {t("components.authentication.legal.and")}
                    <Link
                      href={localizeWebsiteUrl(
                        process.env.REACT_APP_URL_FRONTEND_TERMS_AND_CONDITIONS,
                        activeLang
                      )}
                      target="_blank"
                      underline="none"
                      variant="contentSmallLink"
                      color="inherit"
                    >
                      {` ${t("components.authentication.legal.termsAndConditions")}`}
                    </Link>
                    .
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    pl: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <ModalContentSwitch
                    checked={formPpTac}
                    onChange={(event) => {
                      setFormPpTac(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </Box>
              </Box>

              <Box>
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                  marginTopProp={
                    error === true || warning === true || success === true
                      ? 2
                      : 2
                  }
                  marginBottomProp={
                    error === true || warning === true || success === true
                      ? 2
                      : 0
                  }
                  fullWidth
                />
              </Box>
            </Stack>

            <Box sx={{ mb: 1 }}>
              <B3Small
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setStateStorage((previousState) => ({
                    ...previousState,
                    authState: "login",
                    performRescroll: true,
                  }));

                  let newLocalSettingStorage = JSON.parse(
                    sessionStorage.getItem("myLocalSettingStorage")
                  );
                  if (newLocalSettingStorage === null) {
                    newLocalSettingStorage = stateStorageInit;
                  }

                  newLocalSettingStorage.authState = "login";
                  newLocalSettingStorage.performRescroll = true;
                  sessionStorage.setItem(
                    "myLocalSettingStorage",
                    JSON.stringify(newLocalSettingStorage)
                  );
                }}
              >
                {t("components.authentication.backToLogin")}
              </B3Small>
            </Box>

            <AuthenticationButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!formPpTac}
            >
              {t("components.authentication.createAccount.createButton")}
            </AuthenticationButton>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 1,
                mb: -2,
              }}
            >
              <Typography variant="contentSmall">
                {/* eslint-disable-next-line */}
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    if (caller === "distribution") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        distributionCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.distributionCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    } else if (caller === "distribution-tick-data") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        distributionTickDataCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.distributionTickDataCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    } else if (caller === "promotion") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        promotionCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.promotionCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    }
                  }}
                  underline="none"
                  variant="contentSmallLink"
                  color="inherit"
                >
                  {t("components.authentication.backToPreviousStep")}
                </Link>
              </Typography>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}

export function PasswordForgot({ caller }) {
  PasswordForgot.propTypes = {
    caller: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const wrapperRef = useRef(null);
  const executeRecaptchaV3 = useRecaptchaV3();

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);

  const [returnMessage, setReturnMessage] = useState("");

  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => {
      setResendCooldown((s) => s - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!success && !error) return undefined;
    const timer = setTimeout(() => {
      setSuccess(false);
      setError(false);
      setWarning(false);
      setReturnMessage("");
    }, 8000);
    return () => clearTimeout(timer);
  }, [success, error]);

  const ValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("components.authentication.validation.emailInvalid"))
      .required(t("components.authentication.validation.emailRequired")),
  });

  const handlePasswordReset = async (emailContent, setSubmitting) => {
    setError(false);
    setResendCooldown(60);

    const recaptchaValue = await executeRecaptchaV3("password_reset");
    const sendData = {
      request_type: "checkout",
      route_info: "authentication",
      client: {
        mode: "password_reset",
        email: emailContent.email,
        return_url: `${process.env.REACT_APP_URL_FRONTEND_DASHBOARD}/new-password`,
        // return_url: "http://localhost:3000/new-password",
        recaptcha_value: recaptchaValue,
      },
    };

    backendRequest(process.env.REACT_APP_URL_API_CHECKOUT, sendData)
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("answer");

        if (checkKey === true) {
          if (contentFromBackend.answer.message === "success") {
            setReturnMessage(
              <TranslatedMessage messageKey="components.authentication.passwordForgot.resetEmailSent" />
            );
            setSuccess(true);
            setError(false);
          } else {
            setReturnMessage(
              <TranslatedMessage messageKey="components.authentication.passwordForgot.checkEmailAgain" />
            );
            setError(true);
          }
          setSubmitting(false);
        } else {
          setReturnMessage(
            <TranslatedMessage messageKey="components.authentication.passwordForgot.checkEmailAgain" />
          );
          setError(true);
          setSubmitting(false);
        }
      })
      // eslint-disable-next-line no-console
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage messageKey="components.authentication.errors.unknown" />);
          setError(true);
          setSubmitting(false);
        }
      });
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handlePasswordReset(values, setSubmitting);
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <CardOutlet
      variant="outlet"
      backgroundProp="#F2F2F2"
      title={t("components.authentication.passwordForgot.title")}
      subheader={t("components.authentication.passwordForgot.subheader")}
      mainOutlet={
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={0} sx={{ mt: 1 }}>
              <Box>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.email && errors.email) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.email && errors.email) === true
                    ? errors.email
                    : t("components.authentication.emailLabel")}
                </Typography>
                <AuthenticationTextField
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type="email"
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                />
              </Box>

              <Box>
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                  marginTopProp={
                    error === true || warning === true || success === true
                      ? 1
                      : 0
                  }
                  marginBottomProp={0}
                  fullWidth
                />
              </Box>
            </Stack>

            <Box sx={{ mt: 2, mb: 1 }}>
              <B3Small
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setStateStorage((previousState) => ({
                    ...previousState,
                    authState: "login",
                    performRescroll: true,
                  }));

                  let newLocalSettingStorage = JSON.parse(
                    sessionStorage.getItem("myLocalSettingStorage")
                  );
                  if (newLocalSettingStorage === null) {
                    newLocalSettingStorage = stateStorageInit;
                  }

                  newLocalSettingStorage.authState = "login";
                  newLocalSettingStorage.performRescroll = true;
                  sessionStorage.setItem(
                    "myLocalSettingStorage",
                    JSON.stringify(newLocalSettingStorage)
                  );
                }}
              >
                {t("components.authentication.backToLogin")}
              </B3Small>
            </Box>

            <AuthenticationButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0
                ? t("components.authentication.passwordForgot.sendResetEmailCooldown", {
                    seconds: resendCooldown,
                  })
                : t("components.authentication.passwordForgot.sendResetEmail")}
            </AuthenticationButton>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 1,
                mb: -2,
              }}
            >
              <Typography variant="contentSmall">
                {/* eslint-disable-next-line */}
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    if (caller === "distribution") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        distributionCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.distributionCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    } else if (caller === "distribution-tick-data") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        distributionTickDataCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.distributionTickDataCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    } else if (caller === "promotion") {
                      setStateStorage((previousState) => ({
                        ...previousState,
                        authState: "login",
                        promotionCurrentStep: 0,
                      }));

                      let newLocalSettingStorage = JSON.parse(
                        sessionStorage.getItem("myLocalSettingStorage")
                      );
                      if (newLocalSettingStorage === null) {
                        newLocalSettingStorage = stateStorageInit;
                      }

                      newLocalSettingStorage.authState = "login";
                      newLocalSettingStorage.promotionCurrentStep = 0;

                      sessionStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    }
                  }}
                  underline="none"
                  variant="contentSmallLink"
                  color="inherit"
                >
                  {t("components.authentication.backToPreviousStep")}
                </Link>
              </Typography>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}

export function ApprovalAwaiting({ caller }) {
  ApprovalAwaiting.propTypes = {
    caller: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const [awaitingCount, setAwaitingCount] = useState(0);
  const executeRecaptchaV3 = useRecaptchaV3();

  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);

  const [returnMessage, setReturnMessage] = useState("");

  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => {
      setResendCooldown((s) => s - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const myLocalSettingStorage = JSON.parse(
    sessionStorage.getItem("myLocalSettingStorage")
  );

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => {
      setSuccess(false);
      setReturnMessage("");
    }, 8000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleAwaiting = () => {
    const token = extractDashboardAccessToken(
      myLocalSettingStorage && myLocalSettingStorage.userInformation
    );
    if (!token) {
      // No dashboard_access_token available — the backend didn't return
      // one (e.g. failed-login flow when the email isn't approved). The
      // user can still resend the verification email and use
      // "Back to login" to retry once they've clicked the email link.
      return;
    }
    const sendData = {
      request_type: "checkout",
      route_info: "authentication",
      client: {
        mode: "is_email_approved",
        dashboard_access_token: token,
      },
    };
    backendRequest(process.env.REACT_APP_URL_API_CHECKOUT, sendData).then(
      (contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("answer");

        if (checkKey !== true) return;
        if (contentFromBackend.answer.is_email_approved !== "true") return;

        // Email is now approved. The userInformation we currently hold
        // may be incomplete (the unverified-login flow only stores a
        // stub with the dashboard_access_token + email). Reauthenticate
        // to fetch the wrapped response with account_settings,
        // dashboard_subscriptions and dashboard_promotion — same shape
        // the success-login branch stores — before advancing the step.
        const reauthData = {
          request_type: "checkout",
          route_info: "authentication",
          client: {
            mode: "reauthenticate",
            dashboard_access_token: token,
          },
        };

        backendRequest(
          process.env.REACT_APP_URL_API_CHECKOUT,
          reauthData
        ).then((reauthResponse) => {
          const refreshedUserInformation =
            reauthResponse &&
            Object.keys(reauthResponse).includes("answer")
              ? reauthResponse
              : myLocalSettingStorage.userInformation;

          if (caller === "distribution") {
            const nextStep =
              myLocalSettingStorage.distributionProductAccountRequired === "0"
                ? 3
                : 2;
            setStateStorage((previousState) => ({
              ...previousState,
              distributionCurrentStep: nextStep,
              alreadyApprovedPpTac: true,
              userInformation: refreshedUserInformation,
              ...(nextStep === 3 && {
                distributionAccountType: "",
                distributionAccountNumber: "",
                distributionTradingServer: "",
              }),
            }));

            let newLocalSettingStorage = JSON.parse(
              sessionStorage.getItem("myLocalSettingStorage")
            );
            if (newLocalSettingStorage === null) {
              newLocalSettingStorage = stateStorageInit;
            }

            newLocalSettingStorage.distributionCurrentStep = nextStep;
            newLocalSettingStorage.alreadyApprovedPpTac = true;
            newLocalSettingStorage.userInformation = refreshedUserInformation;
            if (nextStep === 3) {
              newLocalSettingStorage.distributionAccountType = "";
              newLocalSettingStorage.distributionAccountNumber = "";
              newLocalSettingStorage.distributionTradingServer = "";
            }

            sessionStorage.setItem(
              "myLocalSettingStorage",
              JSON.stringify(newLocalSettingStorage)
            );
          } else if (caller === "distribution-tick-data") {
            setStateStorage((previousState) => ({
              ...previousState,
              distributionTickDataCurrentStep: 2,
              alreadyApprovedPpTac: true,
              userInformation: refreshedUserInformation,
            }));

            let newLocalSettingStorage = JSON.parse(
              sessionStorage.getItem("myLocalSettingStorage")
            );
            if (newLocalSettingStorage === null) {
              newLocalSettingStorage = stateStorageInit;
            }

            newLocalSettingStorage.distributionTickDataCurrentStep = 2;
            newLocalSettingStorage.alreadyApprovedPpTac = true;
            newLocalSettingStorage.userInformation = refreshedUserInformation;

            sessionStorage.setItem(
              "myLocalSettingStorage",
              JSON.stringify(newLocalSettingStorage)
            );
          } else if (caller === "promotion") {
            setStateStorage((previousState) => ({
              ...previousState,
              promotionCurrentStep: 2,
              alreadyApprovedPpTac: true,
              userInformation: refreshedUserInformation,
            }));

            let newLocalSettingStorage = JSON.parse(
              sessionStorage.getItem("myLocalSettingStorage")
            );
            if (newLocalSettingStorage === null) {
              newLocalSettingStorage = stateStorageInit;
            }

            newLocalSettingStorage.promotionCurrentStep = 2;
            newLocalSettingStorage.alreadyApprovedPpTac = true;
            newLocalSettingStorage.userInformation = refreshedUserInformation;

            sessionStorage.setItem(
              "myLocalSettingStorage",
              JSON.stringify(newLocalSettingStorage)
            );
          }
        });
      }
    );
  };

  useEffect(() => {
    setTimeout(() => {
      setAwaitingCount(awaitingCount + 1);
      handleAwaiting();
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingCount]);

  const handleResendApprovalEmail = async (emailParam) => {
    if (navigator.onLine === true) {
      setError(false);
      setWarning(false);
      setSuccess(false);

      const recaptchaValue = await executeRecaptchaV3("resend_approval_email");
      const sendData = {
        request_type: "checkout",
        route_info: "authentication",
        client: {
          mode: "resend_approval_email_alt",
          email: emailParam,
          recaptcha_value: recaptchaValue,
        },
      };
      backendRequest(process.env.REACT_APP_URL_API_CHECKOUT, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("answer");

          if (checkKey === true) {
            setReturnMessage(
              <TranslatedMessage messageKey="components.authentication.messages.approvalEmailResent" />
            );
            setSuccess(true);
          } else {
            combineReturnMessage(contentFromBackend, setReturnMessage);
            setError(true);
          }
        })
        .catch(() => {
          if (navigator.onLine === true) {
            setReturnMessage(<TranslatedMessage messageKey="components.authentication.errors.unknown" />);
            setError(true);
          }
        });
    }
  };

  return (
    <CardOutlet
      variant="outlet"
      backgroundProp="#F2F2F2"
      title={t("components.authentication.approvalAwaiting.title")}
      subheader={
        <>
          {t("components.authentication.approvalAwaiting.subheaderBeforeEmail")}{" "}
          <Typography component="span" variant="contentMiddleBold">
            {
              myLocalSettingStorage.userInformation.account_settings
                .config_data.config_data_client.email
            }
          </Typography>
          {t("components.authentication.approvalAwaiting.subheaderAfterEmail")}
        </>
      }
      mainOutlet={
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              mb: -2,
            }}
          >
            <CircularProgress
              size={16}
              thickness={5}
              sx={{ color: "#73E555" }}
            />
            <Typography
              variant="contentMiddle"
              sx={{ color: "#9E9E9E" }}
            >
              {t("components.authentication.approvalAwaiting.waitingForConfirmation")}
            </Typography>
          </Box>

          <Box>
            <RequestMessage
              wrapperRef={wrapperRef}
              error={error}
              warning={warning}
              success={success}
              returnMessage={returnMessage}
              marginTopProp={
                error === true || warning === true || success === true ? 4 : 4
              }
              marginBottomProp={
                error === true || warning === true || success === true ? 2 : 0
              }
              fullWidth
            />
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={12} md={12}>
              <B3Small
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setStateStorage((previousState) => ({
                    ...previousState,
                    authState: "login",
                    performRescroll: true,
                  }));

                  let newLocalSettingStorage = JSON.parse(
                    sessionStorage.getItem("myLocalSettingStorage")
                  );
                  if (newLocalSettingStorage === null) {
                    newLocalSettingStorage = stateStorageInit;
                  }

                  newLocalSettingStorage.authState = "login";
                  newLocalSettingStorage.performRescroll = true;
                  sessionStorage.setItem(
                    "myLocalSettingStorage",
                    JSON.stringify(newLocalSettingStorage)
                  );
                }}
              >
                {t("components.authentication.backToLogin")}
              </B3Small>
            </Grid>
            <Grid item xs={12} md={12}>
              <AuthenticationButton
                fullWidth
                size="large"
                variant="contained"
                onClick={() => {
                  handleResendApprovalEmail(
                    myLocalSettingStorage.userInformation.account_settings
                      .config_data.config_data_client.email
                  );

                  setResendCooldown(60);

                  setStateStorage((previousState) => ({
                    ...previousState,
                    performRescroll: true,
                  }));
                }}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0
                  ? t(
                      "components.authentication.approvalAwaiting.resendVerificationCooldown",
                      { seconds: resendCooldown }
                    )
                  : t(
                      "components.authentication.approvalAwaiting.resendVerification"
                    )}
              </AuthenticationButton>
            </Grid>
          </Grid>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 1,
              mb: -2,
            }}
          >
            <Typography variant="contentSmall">
              {/* eslint-disable-next-line */}
              <Link
                component="button"
                type="button"
                onClick={() => {
                  if (caller === "distribution") {
                    setStateStorage((previousState) => ({
                      ...previousState,
                      authState: "login",
                      distributionCurrentStep: 0,
                    }));

                    let newLocalSettingStorage = JSON.parse(
                      sessionStorage.getItem("myLocalSettingStorage")
                    );
                    if (newLocalSettingStorage === null) {
                      newLocalSettingStorage = stateStorageInit;
                    }

                    newLocalSettingStorage.authState = "login";
                    newLocalSettingStorage.distributionCurrentStep = 0;

                    sessionStorage.setItem(
                      "myLocalSettingStorage",
                      JSON.stringify(newLocalSettingStorage)
                    );
                  } else if (caller === "distribution-tick-data") {
                    setStateStorage((previousState) => ({
                      ...previousState,
                      authState: "login",
                      distributionTickDataCurrentStep: 0,
                    }));

                    let newLocalSettingStorage = JSON.parse(
                      sessionStorage.getItem("myLocalSettingStorage")
                    );
                    if (newLocalSettingStorage === null) {
                      newLocalSettingStorage = stateStorageInit;
                    }

                    newLocalSettingStorage.authState = "login";
                    newLocalSettingStorage.distributionTickDataCurrentStep = 0;

                    sessionStorage.setItem(
                      "myLocalSettingStorage",
                      JSON.stringify(newLocalSettingStorage)
                    );
                  } else if (caller === "promotion") {
                    setStateStorage((previousState) => ({
                      ...previousState,
                      authState: "login",
                      promotionCurrentStep: 0,
                    }));

                    let newLocalSettingStorage = JSON.parse(
                      sessionStorage.getItem("myLocalSettingStorage")
                    );
                    if (newLocalSettingStorage === null) {
                      newLocalSettingStorage = stateStorageInit;
                    }

                    newLocalSettingStorage.authState = "login";
                    newLocalSettingStorage.promotionCurrentStep = 0;

                    sessionStorage.setItem(
                      "myLocalSettingStorage",
                      JSON.stringify(newLocalSettingStorage)
                    );
                  }
                }}
                underline="none"
                variant="contentSmallLink"
                color="inherit"
              >
                {t("components.authentication.backToPreviousStep")}
              </Link>
            </Typography>
          </Box>
        </>
      }
    />
  );
}
